import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import {
    sendClickLog as baseSendClickLog,
    sendDisplayLog as baseSendDisplayLog,
    sendDurationLog as baseSendDurationLog,
    sendInputLog as baseSendInputLog,
    sendLog as baseSendLog,
    sendShowLog as baseSendShowLog,
    sendSystemLog as baseSendSystemLog,
    type LogParams,
    type SendLogOptions,
} from './index';

interface TrackingContextValue {
    commonParams: Partial<LogParams>;
    commonParamsRef: React.RefObject<Partial<LogParams>>;
    setCommonParams: (id: string, params: Partial<LogParams>) => void;
    clearCommonParams: (id: string) => void;
}

const EMPTY_PARAMS: Partial<LogParams> = {};
const TrackingContext = createContext<TrackingContextValue | null>(null);

const isSameParams = (a?: Partial<LogParams>, b?: Partial<LogParams>): boolean => {
    if (a === b) return true;
    if (!a || !b) return false;
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every((key) => {
        const aVal = a[key as keyof LogParams];
        const bVal = b[key as keyof LogParams];
        if (key === 'ext' && typeof aVal === 'object' && typeof bVal === 'object') {
            return JSON.stringify(aVal) === JSON.stringify(bVal);
        }
        return aVal === bVal;
    });
};

const mergeParams = (params: LogParams, commonParams: Partial<LogParams>): LogParams => {
    return {
        ...commonParams,
        ...params,
        ext: {...commonParams.ext, ...params.ext},
    };
};

const mergeParamsMap = (paramsMap: Record<string, Partial<LogParams>>): Partial<LogParams> => {
    const merged: Partial<LogParams> = {};
    Object.values(paramsMap).forEach((params) => {
        const {ext, ...rest} = params;
        Object.assign(merged, rest);
        if (ext) {
            merged.ext = {...merged.ext, ...ext};
        }
    });
    return merged;
};

export const TrackingProvider: React.FC<{children: ReactNode}> = ({children}) => {
    const [paramsMap, setParamsMap] = useState<Record<string, Partial<LogParams>>>({});
    const paramsMapRef = useRef<Record<string, Partial<LogParams>>>({});
    const commonParamsRef = useRef<Partial<LogParams>>(EMPTY_PARAMS);
    const setCommonParams = useCallback((id: string, params: Partial<LogParams>) => {
        if (isSameParams(paramsMapRef.current[id], params)) {
            return;
        }
        const next = {...paramsMapRef.current, [id]: params};
        paramsMapRef.current = next;
        commonParamsRef.current = mergeParamsMap(next);
        setParamsMap(next);
    }, []);
    const clearCommonParams = useCallback((id: string) => {
        if (!paramsMapRef.current[id]) {
            return;
        }
        const next = {...paramsMapRef.current};
        delete next[id];
        paramsMapRef.current = next;
        commonParamsRef.current = mergeParamsMap(next);
        setParamsMap(next);
    }, []);
    const commonParams = useMemo(() => mergeParamsMap(paramsMap), [paramsMap]);
    const value = useMemo(
        () => ({commonParams, commonParamsRef, setCommonParams, clearCommonParams}),
        [clearCommonParams, commonParams, setCommonParams],
    );

    return <TrackingContext.Provider value={value}>{children}</TrackingContext.Provider>;
};

export const useTrackingParams = (params: Partial<LogParams>): void => {
    const context = useContext(TrackingContext);
    const idRef = useRef(`tracking-params-${Math.random().toString(36).slice(2)}`);
    const setCommonParams = context?.setCommonParams;
    const clearCommonParams = context?.clearCommonParams;

    useLayoutEffect(() => {
        setCommonParams?.(idRef.current, params);
    }, [params, setCommonParams]);

    useEffect(() => {
        const id = idRef.current;
        return () => clearCommonParams?.(id);
    }, [clearCommonParams]);
};

export const useTracking = () => {
    const context = useContext(TrackingContext);
    const commonParamsRef = context?.commonParamsRef;

    return useMemo(
        () => ({
            sendLog: (params: LogParams, options?: SendLogOptions) =>
                baseSendLog(mergeParams(params, commonParamsRef?.current ?? EMPTY_PARAMS), options),
            sendClickLog: (params: Omit<LogParams, 'type'>, options?: SendLogOptions) =>
                baseSendClickLog(
                    mergeParams(params as LogParams, commonParamsRef?.current ?? EMPTY_PARAMS),
                    options,
                ),
            sendShowLog: (params: Omit<LogParams, 'type'>, options?: SendLogOptions) =>
                baseSendShowLog(
                    mergeParams(params as LogParams, commonParamsRef?.current ?? EMPTY_PARAMS),
                    options,
                ),
            sendDisplayLog: (params: Omit<LogParams, 'type'> = {}, options?: SendLogOptions) =>
                baseSendDisplayLog(
                    mergeParams(params as LogParams, commonParamsRef?.current ?? EMPTY_PARAMS),
                    options,
                ),
            sendDurationLog: (params: Omit<LogParams, 'type'>, options?: SendLogOptions) =>
                baseSendDurationLog(
                    mergeParams(params as LogParams, commonParamsRef?.current ?? EMPTY_PARAMS),
                    options,
                ),
            sendSystemLog: (params: Omit<LogParams, 'type'>, options?: SendLogOptions) =>
                baseSendSystemLog(
                    mergeParams(params as LogParams, commonParamsRef?.current ?? EMPTY_PARAMS),
                    options,
                ),
            sendInputLog: (params: Omit<LogParams, 'type'>, options?: SendLogOptions) =>
                baseSendInputLog(
                    mergeParams(params as LogParams, commonParamsRef?.current ?? EMPTY_PARAMS),
                    options,
                ),
        }),

        [commonParamsRef],
    );
};
